/**
 * Reusable project-folder picker tree (extracted from AddToProjectButton so
 * other "save into a project folder" flows — e.g. provenance export — reuse the
 * same recursive, keyboard-navigable, permission-aware node instead of
 * duplicating it).
 */
import { useState } from "react";
import {
  Box,
  Chip,
  CircularProgress,
  IconButton,
  Typography,
} from "@mui/material";
import {
  Folder as FolderIcon,
  FolderShared as FolderSharedIcon,
  ChevronRight as ChevronRightIcon,
  ExpandMore as ExpandMoreIcon,
} from "@mui/icons-material";

import { useFolders } from "src/api/projectFolders/projectFolderService";
import { useTreeNavItem } from "src/views/assistant/projects/hooks/useTreeKeyboardNav";
import { isSharedFolder } from "src/api/projectFolders/types";
import type { ProjectFolder } from "src/api/projectFolders/types";
import { treeIndent } from "../helpers/treeIndent";

/** A folder is a valid drop target only when the user can write into it. */
export const isSelectable = (folder: ProjectFolder): boolean =>
  !folder.is_traversal_only &&
  (folder.permission_level === "write" || folder.permission_level === "owner");

interface PickerNodeProps {
  folder: ProjectFolder;
  level: number;
  busy: boolean;
  onPick: (folder: ProjectFolder) => void;
  /** Marks the chat's anchored project, pinned at the top of the picker. */
  isCurrent?: boolean;
}

/**
 * One row in the folder-picker tree: expands to lazy-load children, and is
 * selectable only when writable. Non-writable rows stay traversable (so a user
 * can reach a nested writable folder) but can't be picked.
 */
export const PickerNode: React.FC<PickerNodeProps> = ({
  folder,
  level,
  busy,
  onPick,
  isCurrent = false,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const { data: children, isLoading } = useFolders(
    isExpanded ? folder.id : null,
  );

  const selectable = isSelectable(folder);
  // child_count comes from BE-1; treat missing field as "may have children"
  // so the chevron stays visible on older payloads.
  const mayHaveChildren = (folder.child_count ?? 1) > 0;
  const showChevron = isExpanded
    ? !!children && children.length > 0
    : mayHaveChildren;

  const toggleExpand = (e: React.MouseEvent) => {
    e.stopPropagation();
    if (mayHaveChildren) setIsExpanded((prev) => !prev);
  };

  const handleRowClick = () => {
    if (!selectable) {
      if (mayHaveChildren) setIsExpanded((prev) => !prev);
      return;
    }
    onPick(folder);
  };

  const handleKeyDown = (e: React.KeyboardEvent<HTMLDivElement>) => {
    if (busy) return;
    if (e.key === "Enter" || e.key === " ") {
      e.preventDefault();
      handleRowClick();
    } else if (e.key === "ArrowRight" && mayHaveChildren && !isExpanded) {
      e.preventDefault();
      setIsExpanded(true);
    } else if (e.key === "ArrowLeft" && isExpanded) {
      e.preventDefault();
      setIsExpanded(false);
    }
  };

  const Icon = isSharedFolder(folder) ? FolderSharedIcon : FolderIcon;

  const navItem = useTreeNavItem(folder.id);

  return (
    <Box>
      <Box
        onClick={busy ? undefined : handleRowClick}
        onKeyDown={handleKeyDown}
        role="treeitem"
        {...navItem}
        className={`library-dialog__row${isCurrent ? " is-current" : ""}${
          selectable ? "" : " is-disabled"
        }`}
        aria-disabled={busy || !selectable}
        aria-expanded={mayHaveChildren ? isExpanded : undefined}
        title={
          !selectable
            ? "You don't have write access here — expand to reach nested folders."
            : undefined
        }
        sx={{
          display: "flex",
          alignItems: "center",
          py: 0.5,
          pr: 1,
          pl: treeIndent(level, 0.5),
          borderRadius: 1,
          cursor: busy
            ? "default"
            : selectable
              ? "pointer"
              : mayHaveChildren
                ? "pointer"
                : "default",
          opacity: selectable ? 1 : 0.55,
          backgroundColor: isCurrent ? "action.selected" : undefined,
          "&:hover": {
            backgroundColor:
              busy || (!selectable && !mayHaveChildren)
                ? isCurrent
                  ? "action.selected"
                  : "transparent"
                : "action.hover",
          },
          "&:focus-visible": {
            outline: "2px solid",
            outlineColor: "primary.main",
            outlineOffset: -2,
          },
        }}
      >
        <IconButton
          className="library-dialog__expander"
          size="small"
          tabIndex={-1}
          onClick={toggleExpand}
          aria-label={isExpanded ? "Collapse folder" : "Expand folder"}
          sx={{
            p: 0.25,
            mr: 0.5,
            visibility: showChevron ? "visible" : "hidden",
          }}
        >
          {isLoading ? (
            <CircularProgress size={14} />
          ) : isExpanded ? (
            <ExpandMoreIcon fontSize="small" />
          ) : (
            <ChevronRightIcon fontSize="small" />
          )}
        </IconButton>
        <Icon
          className="library-dialog__row-icon"
          fontSize="small"
          sx={{ mr: 1, color: "text.secondary" }}
        />
        <Typography
          variant="body2"
          className="library-dialog__row-label"
          sx={{
            flex: 1,
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
          }}
        >
          {folder.name}
        </Typography>
        {isCurrent && (
          <Chip
            label="Current"
            size="small"
            variant="outlined"
            sx={{ ml: 1, height: 20 }}
          />
        )}
      </Box>
      {isExpanded && children && children.length > 0 && (
        <Box>
          {children.map((child) => (
            <PickerNode
              key={child.id}
              folder={child}
              level={level + 1}
              busy={busy}
              onPick={onPick}
            />
          ))}
        </Box>
      )}
    </Box>
  );
};
